import { isBefore, isSameSecond, format, add, parseISO, getUnixTime, fromUnixTime, endOfDay, isWithinInterval, getHours, getMinutes, set, getDay, eachDayOfInterval, startOfDay, isAfter, getISOWeek } from 'date-fns';
import { fr } from 'date-fns/esm/locale';
import deepDiff from 'deep-diff';
import { deleteSessionHelpers, sessionsByRecurringUuid, updateReplicaSessions, unparseSessionJsonFields, getSessionById } from './amplify';
import { sortArray, sendNotification, isObjectEmpty, sessionsPerContentType, getTimeFormated, compareSessionPriority, isSessionOverlapping, separateSessionsByWeek, computeHorsesOverlappedSessions, resolveHorseName } from './utils';
import { getHolidaysByPeriod } from './request';
import { generateUuid, checkHorseDailyEffort } from './dataManager';
import { periodType } from './variables';

import store from '@/store';

const replicableFields = ['name', 'discipline', 'horse_size_category', 'product_activity', 'min_participant', 'max_participant', 'place', 'instructors', 'start_time', 'end_time', 'treatment_type'];

const longSessionTypes = ['ABSENCE', 'FERMETURE', 'REPOS', 'SOINS'];

const sessionsWithPeriods = ['STAGE', 'EVENEMENT', 'COMPETITION', 'GROUPE'];

const sessionTypeToNotShowBasedOnTab = {
  customer: ['SOINS', 'FORMATION', 'ADMINISTRATIF', 'ABSENCE', 'REUNION', 'PERMANENCE'],
  place: ['FORMATION', 'ADMINISTRATIF', 'ABSENCE', 'REUNION', 'PERMANENCE'],
  horse: ['FORMATION', 'ADMINISTRATIF', 'ABSENCE', 'REUNION', 'PERMANENCE']
};

const activityToSessionTypes = {
  EQUITATION: ['SESSION_REPRISE', 'FORMATION', 'ADMINISTRATIF', 'REUNION', 'HORS_MONTOIR', 'COMPETITION', 'REPOS', 'SOINS', 'EVENEMENT', 'ABSENCE', 'SAILLIES', 'FERMETURE', 'GROUPE', 'STAGE', 'AUTRE'],
  GOLF: ['GOLF'],
  TENNIS: []
};

const recurringHorseAssignSessions = ['HORS_MONTOIR', 'SOINS', 'STAGE', 'SESSION_REPRISE'];

const canCopyHorses = ['SESSION_REPRISE', 'STAGE'];

const sessionsHorsesPriority = {
  SOINS: 1,
  HORS_MONTOIR: 2,
  OTHER: 3
};

const NO_UPDATE = 'NO_UPDATE';

// when creating a session we can't create a session type REPRISE (horanet session)
const getReferencesSessionCreate = () => {
  const { references } = store.state;
  if (references && !isObjectEmpty(references)) {
    const types = Object.entries(references.equestrian_session_values || {}).map(([key, value]) => ({
      value: key,
      label: value
    }));
    // we can't create session of type session reprise because this type are sessions that come from horanet
    // const index = types.findIndex(type => type.value === 'SESSION_REPRISE');

    // if (index !== -1) {
    //   types.splice(index, 1);
    // }

    return sortArray(types, 'label');
  }
  return [];
};

function isParticipantAmountOk (session) {
  if ((session.min_participant && session.min_participant < 0) || (session.max_participant && session.max_participant < 0)) {
    return false;
  }
  if (session.min_participant && session.max_participant && session.max_participant < session.min_participant) {
    return false;
  }

  return true;
}

const constructSessionsBag = async (option, newSession, originalStartTime = newSession.start_time, periodEndDate = null) => {
  const endDate = periodEndDate || getUnixTime(endOfDay(fromUnixTime(newSession.end_recurring_date))) + 1;

  const sessions = [];
  switch (option) {
    case 'SINGLE':
      sessions.push(newSession);
      break;
    case 'FOLLOWING': {
      const followingSessions = await sessionsByRecurringUuid(newSession.recurring_uuid, originalStartTime, endDate);
      followingSessions.forEach(session => sessions.push(session));
      break;
    }
    case 'ALL': {
      const allSessions = await sessionsByRecurringUuid(newSession.recurring_uuid);
      allSessions.forEach(session => sessions.push(session));
      break;
    }
    case 'FOLLOWING_DAY': {
      const { dailySessions } = store.getters;

      const filteredSessions = dailySessions.filter(s => s.recurring_uuid === newSession.recurring_uuid && isWithinInterval(new Date(fromUnixTime(s.start_time)), { start: new Date(fromUnixTime(newSession.start_time)), end: endOfDay(new Date(fromUnixTime(newSession.start_time))) }));

      filteredSessions.forEach(session => sessions.push(session));

      break;
    }
    case 'ALL_DAY': {
      const { dailySessions } = store.getters;

      const filteredSessions = dailySessions.filter(s => s.recurring_uuid === newSession.recurring_uuid);

      filteredSessions.forEach(session => sessions.push(session));
      break;
    }
    default:
      sessions.push(newSession);
  }

  return sessions;
};

const getDatesBetweenDates = (start, end) => {
  const dates = [];
  let startDate = start;

  while (startDate !== end) {
    const current = startDate;
    dates.push(current);
    startDate = format(add(new Date(parseISO(startDate)), { days: 1 }), 'yyyy-MM-dd');
  }

  // add end date
  dates.push(end);
  return dates;
};

/**
 * session uuid = startTime(in timestamp in sec) _ 00 (based on the amount of session that will occur in the same hour)
 * this function will get the first available number between 0 and 99
 */
const computeSessionUuid = session => `session_${session.start_time}_${generateUuid()}`;

function getSessionFieldByWtype (wtype) {
  switch (wtype) {
    case 'customer': {
      return 'participants';
    }
    case 'place': {
      return 'place';
    }
    case 'collaborator':
      return 'instructors';
    default:
      return `${wtype}s`;
  }
}

// get content type session
function sessionContentType (array, field, session) {
  if (field === 'place') {
    if (session && session[field] && !isObjectEmpty(session[field])) {
      return array.find(contentT => contentT.uuid === session[field].uuid);
    }
    return null;
  }
  return (session[field] || []).map(ct => array.find(contentT => contentT.uuid === ct.uuid));
}

function filterSessionByInterval (sessions, start, end) {
  return (sessions || []).filter(session => isWithinInterval(new Date(session.start_time * 1000), { start, end }));
}

function getOverlappedSessions (sessions, session) {
  return sessions.filter(s => s.id !== session.id).filter(s => {
    // session is inside selected session time interval
    if (isWithinInterval(fromUnixTime(s.start_time), { start: fromUnixTime(session.start_time), end: fromUnixTime(session.end_time) }) && isWithinInterval(fromUnixTime(s.end_time), { start: fromUnixTime(session.start_time), end: fromUnixTime(session.end_time) })) {
      return true;
    }

    // session that start before selected session but ends while its ongoing
    if (s.start_time <= session.start_time && (isWithinInterval(add(fromUnixTime(s.end_time), { minutes: -1 }), { start: new Date(session.start_time * 1000), end: new Date(session.end_time * 1000) }))) {
      return true;
    }

    // session that starts while selected session is ongoing but ends after
    if ((isWithinInterval(add(fromUnixTime(s.start_time), { minutes: 1 }), { start: new Date(session.start_time * 1000), end: new Date(session.end_time * 1000) })) && s.end_time >= session.end_time) {
      return true;
    }

    // selected session is inside a session time interval
    if (s.start_time <= session.start_time && s.end_time >= session.end_time) {
      return true;
    }

    return false;
  });
}

function getContentTypeSessionsByPeriod (start, end, contentType, sessions = store.state.contentTypeAllSessions) {
  const sessionField = getSessionFieldByWtype(contentType.wtype);

  const filteredSessions = filterSessionByInterval(sessions, start, end);
  return sessionsPerContentType(contentType, sessionField, filteredSessions);
}

function normalizedSessionValuesForPeriod (sessionPeriod, day) {
  const normalizedPeriodStart = getUnixTime(new Date(`${day}T${getTimeFormated(fromUnixTime(sessionPeriod.start))}`));
  const normalizedPeriodEnd = getUnixTime(new Date(`${day}T${getTimeFormated(fromUnixTime(sessionPeriod.end))}`));

  const startHoursPeriod = getHours(new Date(fromUnixTime(normalizedPeriodStart)));
  const startMinutesPeriod = getMinutes(new Date(fromUnixTime(normalizedPeriodStart)));
  const endHoursPeriod = getHours(new Date(fromUnixTime(normalizedPeriodEnd)));
  const endMinutesPeriod = getMinutes(new Date(fromUnixTime(normalizedPeriodEnd)));

  return {
    periodDiscipline: sessionPeriod.discipline,
    normalizedPeriodStart,
    normalizedPeriodEnd,
    startHoursPeriod,
    startMinutesPeriod,
    endHoursPeriod,
    endMinutesPeriod
  };
}

async function filterHolidaysFromReplicas (days, selectedDate, replica_end_date, workspaceCode, excludeHolidays, excludeVacations) {
  const daysToReturn = [...days];

  if (excludeHolidays || excludeVacations) {
    const response = await getHolidaysByPeriod(getUnixTime(new Date(selectedDate)), getUnixTime(new Date(replica_end_date)), workspaceCode);

    if (response && (!response.success || !response.data.length)) {
      return daysToReturn;
    }

    let holidays = response.data;

    if (excludeVacations && !excludeHolidays) {
      holidays = holidays.filter(holiday => holiday.type === periodType.VACATION);
    }
    if (!excludeVacations && excludeHolidays) {
      holidays = holidays.filter(holiday => holiday.type === periodType.HOLIDAY);
    }

    return daysToReturn.filter(day => !holidays.some(holiday => isWithinInterval(new Date(day), { start: new Date(holiday.startTimestamp * 1000), end: new Date(holiday.endTimestamp * 1000) })));
  }
  return daysToReturn;
}

function constructReplicaSessionBag (excludeVacations, excludeHolidays, days, session, replicaType, replicaOption, sessionsBag, replicaEndDate, interval = [], weeks = []) {
  const sessionToCreate = deleteSessionHelpers(session);
  const recurring_uuid = generateUuid();

  const replicaIntervalChanges = sessionToCreate.end_recurring_date ? format(new Date(fromUnixTime(sessionToCreate.end_recurring_date)), 'yyyy-MM-dd') !== replicaEndDate : true;

  const replicaSessions = [];

  const periods = { };

  days.map(day => {
    let sessionReplica = {
      ...sessionToCreate,
      recurring: true,
      recurring_uuid: replicaIntervalChanges ? recurring_uuid : sessionToCreate.recurring_uuid,
      horses: [],
      participants: [],
      replica: {
        replica_type: replicaType,
        replica_option: replicaOption,
        replica_interval: interval,
        replica_weeks: weeks,
        excludeHolidays,
        excludeVacations
      }
    };

    if (sessionToCreate.periods.length) {
      if (isObjectEmpty(periods)) {
        sessionToCreate.periods.forEach((sessionPeriod, index) => {
          periods[index] = generateUuid();
        });
      }
      sessionToCreate.periods.forEach((sessionPeriod, index) => {
        const {
          normalizedPeriodStart,
          normalizedPeriodEnd,
          startHoursPeriod,
          startMinutesPeriod,
          endHoursPeriod,
          endMinutesPeriod,
          periodDiscipline
        } = normalizedSessionValuesForPeriod(sessionPeriod, day);

        sessionReplica = {
          ...sessionReplica,
          uuid: computeSessionUuid({ start_time: normalizedPeriodStart }),
          start_time: normalizedPeriodStart,
          end_time: normalizedPeriodEnd,
          start_recurring_date: getUnixTime(set(parseISO(days[0]), { hours: startHoursPeriod, minutes: startMinutesPeriod })),
          end_recurring_date: getUnixTime(set(parseISO(days[days.length - 1]), { hours: endHoursPeriod, minutes: endMinutesPeriod })),
          periods: sessionReplica.periods.map((p, periodIndex) => {
            if (periodIndex === index) {
              p.periodID = periods[index];
            }

            const { discipline, ...periodBody } = p;

            return {
              ...periodBody,
              start: getUnixTime(new Date(`${day}T${format(new Date(fromUnixTime(p.start)), 'HH:mm')}`)),
              end: getUnixTime(new Date(`${day}T${format(new Date(fromUnixTime(p.end)), 'HH:mm')}`))
            };
          })
        };

        replicaSessions.push({
          ...sessionReplica,
          discipline: sessionReplica.discipline || periodDiscipline
        });
      });
      return;
    }

    const startHours = getHours(new Date(fromUnixTime(sessionToCreate.start_time)));
    const startMinutes = getMinutes(new Date(fromUnixTime(sessionToCreate.start_time)));
    const endHours = getHours(new Date(fromUnixTime(sessionToCreate.end_time)));
    const endMinutes = getMinutes(new Date(fromUnixTime(sessionToCreate.end_time)));
    const start_time = getUnixTime(set(new Date(day), { hours: startHours, minutes: startMinutes }));
    const end_time = getUnixTime(set(new Date(day), { hours: endHours, minutes: endMinutes }));

    const sessionExist = sessionsBag.find(s => isWithinInterval(new Date(s.start_time * 1000), { start: startOfDay(new Date(day)), end: endOfDay(new Date(day)) }));

    // *Info: to prevent existing one if the session have start recurring it will always be start of day
    sessionReplica = {
      ...sessionReplica,
      uuid: computeSessionUuid({ start_time }),
      start_time: start_time,
      end_time: end_time,
      start_recurring_date: sessionToCreate.start_recurring_date ? getUnixTime(startOfDay(new Date(fromUnixTime(sessionToCreate.start_recurring_date)))) : getUnixTime(startOfDay(parseISO(days[0]))),
      end_recurring_date: sessionToCreate.end_recurring_date ? getUnixTime(endOfDay(new Date(fromUnixTime(sessionToCreate.end_recurring_date)))) : getUnixTime(endOfDay(parseISO(days[days.length - 1])))
    };

    // if (replicaType === 'REPLICATION_SIMPLE') {
    if (sessionExist) {
      sessionReplica.horses = sessionExist.horses;
      sessionReplica.participants = sessionExist.participants;
    }
    replicaSessions.push(sessionReplica);
    // }
  });

  return replicaSessions;
}

async function computeReplicaDays (session, replica, changeStartDateComparation = true) {
  const { replica_end_date, replica_type, replica_option, replica_interval, replica_weeks, excludeHolidays, excludeVacations } = replica;
  const { selectedDate } = store.getters;
  const { workspace } = store.state;

  let data = selectedDate;
  if (session.start_recurring_date && changeStartDateComparation) {
    data = format(fromUnixTime(session.start_recurring_date), 'yyyy-MM-dd');
  }

  // request date from start till end
  if (replica_type === 'REPLICATION_SIMPLE') {
    switch (replica_option) {
      case 'ALL_JOURS': {
        const days = getDatesBetweenDates(data, replica_end_date);

        return filterHolidaysFromReplicas(days, selectedDate, replica_end_date, workspace.code, excludeHolidays, excludeVacations);
      }
      case 'ALL_WEEK': {
        const weekDays = eachDayOfInterval({ start: startOfDay(new Date(data)), end: new Date(replica_end_date) });
        const days = weekDays.filter(day => getDay(day) === getDay(new Date(data)));
        const daysToReturn = days.map(day => format(day, 'yyyy-MM-dd'));

        return filterHolidaysFromReplicas(daysToReturn, selectedDate, replica_end_date, workspace.code, excludeHolidays, excludeVacations);
      }
      default: {
        return [];
      }
    }
  } else {
    const days = eachDayOfInterval({ start: new Date(selectedDate), end: new Date(replica_end_date) });
    let daysToReturn = days.map(day => format(day, 'yyyy-MM-dd'));

    daysToReturn = await filterHolidaysFromReplicas(daysToReturn, selectedDate, replica_end_date, workspace.code, excludeHolidays, excludeVacations);

    return daysToReturn.filter(day => replica_interval.includes(getDay(new Date(day))) && !replica_weeks.includes(getISOWeek(new Date(day))));
  }
}

async function handleSimpleReplication (date, session, option, sessionsBag, editOption, excludeHolidays, excludeVacations) {
  const changeStartDateComparation = editOption !== 'FOLLOWING';

  const days = await computeReplicaDays(session, { replica_type: 'REPLICATION_SIMPLE', replica_option: option, replica_end_date: date, excludeHolidays, excludeVacations }, changeStartDateComparation);

  return constructReplicaSessionBag(excludeVacations, excludeHolidays, days, session, 'REPLICATION_SIMPLE', option, sessionsBag, date);
}

async function handleAdvanceReplication (date, session, interval, weeks, sessionsBag, excludeHolidays, excludeVacations) {
  const days = await computeReplicaDays(session, { replica_type: 'REPLICATION_ADVANCE', replica_end_date: date, replica_interval: interval, replica_weeks: weeks, excludeHolidays, excludeVacations });
  return constructReplicaSessionBag(excludeVacations, excludeHolidays, days, session, 'REPLICATION_ADVANCE', null, sessionsBag, date, interval, weeks);
}

function handeReplicationSession (editOption, { replica_end_date, replica_type, replica_option, replica_interval, replica_weeks, excludeHolidays, excludeVacations }, session, sessionsBag = []) {
  if (replica_type === 'REPLICATION_SIMPLE') {
    return handleSimpleReplication(replica_end_date, session, replica_option, sessionsBag, editOption, excludeHolidays, excludeVacations);
  }
  // advance replication
  return handleAdvanceReplication(replica_end_date, session, replica_interval, replica_weeks, sessionsBag, excludeHolidays, excludeVacations);
}

function isEndDateBeforeStart (session) {
  const isEndDateBefore = isBefore(new Date(fromUnixTime(session.end_time)), new Date(fromUnixTime(session.start_time)));
  const isSameDate = isSameSecond(new Date(fromUnixTime(session.end_time)), new Date(fromUnixTime(session.start_time)));
  return isEndDateBefore || isSameDate;
}

function isEndTimeAllowedToCreate (sessionToCreate, sendNotificationMessage = true) {
  if (isEndDateBeforeStart(sessionToCreate)) {
    if (sendNotificationMessage) {
      const text = 'L\'heure de fin doit être supérieure à l\'heure de début';
      sendNotification('error', text);
    }
    return false;
  }
  return true;
}

function computePeriodSession (session) {
  return session.periods.map(period => ({
    ...session,
    discipline: session.discipline || period.discipline,
    start_time: period.start,
    end_time: period.end
  }));
}

async function handleAmountOfSessions (session, sessionReplica, editOption = 'SINGLE', existingSessionsBag = []) {
  const sessionsToHandle = [];
  const sessionsToCreate = [];
  const recurring_uuid = generateUuid();

  if (session.periods.length && !sessionReplica.isReplica) {
    const sessionPeriods = computePeriodSession(session);
    const recurringUuid = generateUuid();
    const sortedPeriods = sortArray(sessionPeriods, 'start_time');
    sessionPeriods.map(s => {
      const sessAux = { ...s, recurring_uuid: recurringUuid, recurring: true, start_recurring_date: getUnixTime(startOfDay(new Date(fromUnixTime(sortedPeriods[0].start_time)))), end_recurring_date: getUnixTime(endOfDay(new Date(fromUnixTime(sortedPeriods[sortedPeriods.length - 1].start_time)))) };

      sessAux.periods = sessAux.periods.map(p => {
        const { discipline, ...periodToSave } = p;
        return { ...periodToSave, periodID: generateUuid() };
      });

      sessionsToHandle.push(sessAux);
    });
  } else {
    // for recurring long type sessions
    const start = format(new Date(fromUnixTime(session.start_time)), 'yyyy-MM-dd');
    const end = format(new Date(fromUnixTime(session.end_date)), 'yyyy-MM-dd');
    if (!!session.allDay && start !== end && !sessionReplica.isReplica) {
      const datesToCreate = getDatesBetweenDates(start, end);
      datesToCreate.forEach(date => {
        const startDay = getUnixTime(set(new Date(date), { hours: 8 }));
        const endDay = getUnixTime(add(endOfDay(new Date(parseISO(date))), { seconds: 1 }));

        const sessionToCreate = {
          ...session,
          start_time: startDay,
          end_time: endDay,
          recurring: true,
          recurring_uuid: recurring_uuid,
          start_recurring_date: getUnixTime(new Date(datesToCreate[0])),
          end_recurring_date: getUnixTime(endOfDay(new Date(datesToCreate[datesToCreate.length - 1])))
        };

        sessionsToCreate.push(sessionToCreate);
      });
    }

    if (start === end || sessionReplica.isReplica || (start !== end && !session.allDay)) {
      sessionsToHandle.push(session);
    }
  }

  const promises = sessionsToHandle.map(async sessionCreate => {
    // handler of replication simple and advance
    if (sessionReplica.isReplica) {
      const replicationSytem = await handeReplicationSession(editOption, sessionReplica, sessionCreate, existingSessionsBag);
      replicationSytem.forEach(replicaSession => sessionsToCreate.push(replicaSession));
      return;
    }
    sessionsToCreate.push(sessionCreate);
  });
  await Promise.all(promises);
  return sessionsToCreate;
}

function addPeriodToSessionPeriodArray (session) {
  const newSession = { ...session };

  if (!newSession.periods.length) {
    newSession.periods.push({ start: newSession.start_time, end: newSession.end_time });
    return newSession.periods;
  }
  // maybe keep the time
  newSession.periods.push({ start: null, end: null });

  return newSession.periods;
}

function sessionHaveError (session) {
  const { startTimeHelper, endTimeHelper, date, id, uuid, pre_reserved_participants, ...sessionBody } = session;
  const dateFormated = format(fromUnixTime(date), 'yyyy-MM-dd');
  sessionBody.start_time = getUnixTime(new Date(`${dateFormated}T${startTimeHelper}`));
  sessionBody.end_time = getUnixTime(new Date(`${dateFormated}T${endTimeHelper}`));

  return !isEndTimeAllowedToCreate(sessionBody, false);
}

function prepareSessionTransaction (session) {
  let sessionBody = { ...session };

  const { pre_reserved_participants, ...sess } = sessionBody;

  sessionBody = unparseSessionJsonFields(sess);

  sessionBody = deleteSessionHelpers(sessionBody);

  return sessionBody;
}

async function handleSessionsCreation (sessions) {
  const body = {
    create: [],
    edit: [],
    delete: []
  };
  sessions.map(s => body.create.push(prepareSessionTransaction(deleteSessionHelpers(s))));

  return updateReplicaSessions(body, store.getters.amplifyWorkspace);
}

async function handleDeleteSession (deleteOption, session) {
  const sessionsToDelete = await constructSessionsBag(deleteOption, session);

  const body = {
    create: [],
    edit: [],
    delete: []
  };

  sessionsToDelete.map(s => {
    body.delete.push({ id: s.id });
  });

  if (sessionsWithPeriods.includes(session.type) && session.recurring && session.recurring_uuid) {
    const { dailySessions } = store.getters;

    let relatedPeriodSessions = dailySessions.filter(s => s.recurring_uuid === session.recurring_uuid);

    const dailiesToDelete = relatedPeriodSessions.filter(s => sessionsToDelete.some(sess => sess.id === s.id));

    relatedPeriodSessions = relatedPeriodSessions.filter(s => !sessionsToDelete.some(sess => sess.id === s.id));

    relatedPeriodSessions = relatedPeriodSessions.map(sess => {
      // remove the periods from  dailiesToDelet
      dailiesToDelete.map(s => {
        const periodIndex = sess.periods.findIndex(p => p.start === s.start_time && p.end === s.end_time);

        if (periodIndex !== -1) {
          sess.periods.splice(periodIndex, 1);
        }
      });

      return sess;
    });

    relatedPeriodSessions.map(s => body.edit.push(prepareSessionTransaction(deleteSessionHelpers(s))));
  }
  return updateReplicaSessions(body, store.getters.amplifyWorkspace);
}

function prepareSessionBody (session, sessionContents, sessionReplica) {
  const sessionToCreate = { ...session };
  const { selectedDate, dailySessions } = store.getters;
  if (sessionToCreate.allDay && longSessionTypes.includes(sessionToCreate.type)) {
    sessionToCreate.start_time = getUnixTime(set(new Date(parseISO(selectedDate)), { hours: 8 }));
    sessionToCreate.end_time = getUnixTime(endOfDay(new Date(parseISO(selectedDate))));
  }

  // sessionToCreate.recurring_uuid = sessionToCreate.recurring_uuid ? sessionToCreate.recurring_uuid : generateUuid();

  if (!sessionToCreate.recurring_uuid) {
    delete sessionToCreate.recurring_uuid;
  }

  if (sessionToCreate.recurring || sessionToCreate.allDay) {
    sessionToCreate.recurring_uuid = sessionToCreate.recurring_uuid || generateUuid();
    sessionToCreate.start_recurring_date = sessionToCreate.start_recurring_date || sessionToCreate.start_time;
    sessionToCreate.end_recurring_date = sessionToCreate.end_recurring_date || (sessionReplica.isReplica ? getUnixTime(endOfDay(new Date(fromUnixTime(sessionReplica.replica_end_date)))) : null) || getUnixTime(endOfDay(new Date(fromUnixTime(sessionToCreate.end_date))));
  }

  sessionToCreate.place = {};

  if (sessionContents.place && !isObjectEmpty(sessionContents.place)) {
    sessionToCreate.place = {
      uuid: sessionContents.place.uuid,
      wtitle: sessionContents.place.wtitle
    };
  }

  if (!isParticipantAmountOk(sessionToCreate)) {
    return { success: false, message: 'Montant de participant incorrect', code: 'INCORRECT_PARTICIPANT_AMOUT' };
  }

  const canAvance = isEndTimeAllowedToCreate(sessionToCreate);

  if (!canAvance) {
    return { success: false, message: 'L\'heure de fin doit être supérieure à l\'heure de début' };
  }

  sessionToCreate.replica = null;

  if (sessionToCreate.allDay && !sessionReplica.isReplica) {
    sessionToCreate.replica = {
      replica_type: 'REPLICATION_SIMPLE',
      replica_option: 'ALL_JOURS',
      replica_interval: [],
      replica_weeks: [],
      excludeHolidays: false
    };
  }

  if (sessionReplica.isReplica) {
    sessionToCreate.replica = {
      excludeHolidays: sessionReplica.excludeHolidays,
      replica_type: sessionReplica.replica_type,
      replica_option: sessionReplica.replica_option,
      replica_interval: sessionReplica.replica_interval,
      replica_weeks: sessionReplica.replica_weeks
    };
  }

  return { success: true, data: sessionToCreate };
}

async function handleCreateSession (sessionToCreate, sessionReplica, editOption = 'SINGLE', keepHorseAndParticipants = false) {
  const periodsOverlapping = (sessionToCreate?.periods || []).length && (sessionToCreate.periods || []).some((p, index) => (sessionToCreate.periods || []).slice(index + 1).some(p2 => p.start !== p2.start && p.end !== p2.end && p.start < p2.end && p.end > p2.start));
  if (periodsOverlapping) {
    return { success: false, message: 'L\'enregistrement n\'a pas été possible : il y a des périodes qui se chevauchent.' };
  }
  let sessionsToCreate = await handleAmountOfSessions(sessionToCreate, sessionReplica, editOption);

  if (keepHorseAndParticipants) {
    sessionsToCreate = sessionsToCreate.map(s => {
      const isInInterval = isWithinInterval(new Date(fromUnixTime(s.start_time)), { start: startOfDay(fromUnixTime(sessionToCreate.start_time)), end: endOfDay(fromUnixTime(sessionToCreate.end_time)) });
      if (isInInterval) {
        s.horses = sessionToCreate.horses;
        s.participants = sessionToCreate.participants;
      }
      return s;
    });
  }

  const everySessionTimeIsCorrect = sessionsToCreate.every(session => isEndTimeAllowedToCreate(session));

  if (!everySessionTimeIsCorrect) {
    return { success: false, message: 'merci de corriger les erreurs' };
  }

  const body = {
    create: sessionsToCreate.map(s => prepareSessionTransaction(deleteSessionHelpers({ ...s, id: generateUuid(), uuid: computeSessionUuid(s), workspace: store.getters.amplifyWorkspace }))),
    edit: [],
    delete: []
  };

  return updateReplicaSessions(body, store.getters.amplifyWorkspace);
}

const applyProjectedFieldsToSessions = (sessions, sessionToEdit, fieldsToUpdate) => sessions.map(session => {
  const projected = { ...session };

  fieldsToUpdate.forEach(field => {
    if (!replicableFields.includes(field)) return;

    if (field === 'start_time' || field === 'end_time') {
      const hours = getHours(fromUnixTime(sessionToEdit[field]));
      const minutes = getMinutes(fromUnixTime(sessionToEdit[field]));
      projected[field] = getUnixTime(set(fromUnixTime(session[field]), { hours, minutes }));
    } else {
      projected[field] = sessionToEdit[field];
    }
  });

  return projected;
});

const computeHorseOverlapConflicts = async (projectedSessions, overlappedSessions, horses) => {
  const conflicts = [];

  if (!store.state.isLazyLoaded.horses) {
    await store.dispatch('getHorses');
  }

  (horses || []).forEach(h => {
    const horse = (store.state.horses || []).find(sh => sh.uuid === h.uuid) || h;
    const sessionsWithHorse = overlappedSessions.filter(overlappedSession => overlappedSession.horses.some(he => he.uuid === horse.uuid));

    (projectedSessions || []).forEach(projectedSession => {
      const sessionsWithOverlap = sessionsWithHorse.filter(session => isSessionOverlapping(session, projectedSession));

      (sessionsWithOverlap || []).forEach(overlappedSession => {
        // Skip if the overlapped session is also being edited (in projectedSessions)
        if (projectedSessions.some(ps => ps.id === overlappedSession.id)) {
          return;
        }

        const editedWins = compareSessionPriority(overlappedSession, projectedSession);
        const staySession = editedWins ? projectedSession : overlappedSession;
        const removeSession = editedWins ? overlappedSession : projectedSession;

        conflicts.push({
          horse,
          projectedSession,
          overlappedSession,
          editedWins,
          stayMessage: `${horse.horse_name || horse.wtitle || 'inconnu'} reste sur ${staySession.name} (${format(fromUnixTime(staySession.start_time), 'dd/MM/yyyy HH:mm', { locale: fr })} - ${format(fromUnixTime(staySession.end_time), 'HH:mm', { locale: fr })})`,
          removeMessage: `${horse.horse_name || horse.wtitle || 'inconnu'} retiré de ${removeSession.name} (${format(fromUnixTime(removeSession.start_time), 'dd/MM/yyyy HH:mm', { locale: fr })} - ${format(fromUnixTime(removeSession.end_time), 'HH:mm', { locale: fr })})`
        });
      });
    });
  });

  return conflicts;
};

const mergeSessionUpdatesIntoBody = (bodyEdit, modifiedSessions) => {
  const mergedEdit = [...bodyEdit];

  (modifiedSessions || []).forEach(modifiedSession => {
    const existingIndex = mergedEdit.findIndex(e => e.id === modifiedSession.id);

    if (existingIndex !== -1) {
      mergedEdit[existingIndex] = {
        ...mergedEdit[existingIndex],
        horses: modifiedSession.horses !== undefined ? modifiedSession.horses : mergedEdit[existingIndex].horses,
        participants: modifiedSession.participants !== undefined ? modifiedSession.participants : mergedEdit[existingIndex].participants,
        instructors: modifiedSession.instructors !== undefined ? modifiedSession.instructors : mergedEdit[existingIndex].instructors
      };
    } else {
      mergedEdit.push(modifiedSession);
    }
  });

  return mergedEdit;
};
const updateSessionsBulk = async (sessions, contentTypes) => {
  const body = {
    create: [],
    edit: [],
    delete: []
  };

  if ((sessions || []).some(s => s.start_time && s.end_time && s.end_time <= s.start_time)) {
    sendNotification('error', 'L\'heure de fin doit être supérieure à l\'heure de début');
    return false;
  }

  let recurringUuid = null;

  body.edit = sessions.map(s => {
    recurringUuid = s.recurring_uuid;
    return prepareSessionTransaction({ id: s.id, ...contentTypes.reduce((acc, contentType) => ({ ...acc, [contentType]: s[contentType] }), {}) });
  });

  const response = await updateReplicaSessions(body, store.getters.amplifyWorkspace, recurringUuid);

  if (response) {
    sendNotification(response.success ? 'success' : 'error', response.message);
    return true;
  }
  sendNotification('error', 'Quelque chose s\'est mal passé');
  return false;
};

const addContentTypeToUpdate = (aditionalContentTypes, contentType) => {
  if (!aditionalContentTypes.includes(contentType)) {
    aditionalContentTypes.push(contentType);
  }
  return aditionalContentTypes;
};

const addHorseToSession = (session, horse, sessionsToUpdate = {}, aditionalContentTypes = [], ownerParticipant = null, isInstructor = false, allowSwap = false) => {
  const sessionUpdate = sessionsToUpdate[session.id] || {
    id: session.id,
    horses: [...session.horses || []],
    participants: [...session.participants || []],
    instructors: [...session.instructors || []]
  };

  const oldParticipantIndex = sessionUpdate.participants.findIndex(p => p.horse === horse.uuid);
  const oldInstructorIndex = sessionUpdate.instructors.findIndex(i => i.horse === horse.uuid);
  /* Safeguard to avoid removing a assigned horse from a person in a session */
  const horseAssignedToPerson = oldParticipantIndex !== -1 || oldInstructorIndex !== -1;

  if (horseAssignedToPerson && !allowSwap) {
    return sessionUpdate;
  }

  if (allowSwap) {
    if (oldParticipantIndex !== -1) {
      sessionUpdate.participants[oldParticipantIndex].horse = null;
      addContentTypeToUpdate(aditionalContentTypes, 'participants');
    }

    if (oldInstructorIndex !== -1) {
      sessionUpdate.instructors[oldInstructorIndex].horse = null;
      addContentTypeToUpdate(aditionalContentTypes, 'instructors');
    }
  }

  const horseIndex = sessionUpdate.horses.findIndex(h => h.uuid === horse.uuid);

  if (horseIndex !== -1) {
    sessionUpdate.horses[horseIndex] = { ...sessionUpdate.horses[horseIndex], wtitle: resolveHorseName(horse), isInstructorHorse: isInstructor };
  } else {
    sessionUpdate.horses.push({ uuid: horse.uuid, wtitle: resolveHorseName(horse), isInstructorHorse: isInstructor });
  }

  if (ownerParticipant) {
    const participantIndex = sessionUpdate.participants.findIndex(p => p.uuid === ownerParticipant.uuid);

    if (participantIndex !== -1) {
      sessionUpdate.participants[participantIndex] = { ...ownerParticipant, horse: horse.uuid };
    } else {
      sessionUpdate.participants.push({ ...ownerParticipant, horse: horse.uuid });
    }

    if (!aditionalContentTypes.includes('participants')) {
      aditionalContentTypes.push('participants');
    }
    return sessionUpdate;
  }

  if (horse?.participant) {
    const horseParticipantIndex = sessionUpdate.participants.findIndex(p => p.uuid === horse.participant);

    if (horseParticipantIndex !== -1) {
      sessionUpdate.participants[horseParticipantIndex].horse = horse.uuid;
    } else {
      sessionUpdate.participants.push({ uuid: horse.participant, horse: horse.uuid });
    }

    addContentTypeToUpdate(aditionalContentTypes, 'participants');
  }

  if (horse?.instructor) {
    const horseInstructorIndex = sessionUpdate.instructors.findIndex(i => i.uuid === horse.instructor);

    if (horseInstructorIndex !== -1) {
      sessionUpdate.instructors[horseInstructorIndex].horse = horse.uuid;
    } else {
      sessionUpdate.instructors.push({ uuid: horse.instructor, horse: horse.uuid });
    }

    addContentTypeToUpdate(aditionalContentTypes, 'instructors');
  }

  return sessionUpdate;
};

const removeHorseFromSession = (session, horse, aditionalContentTypes, sessionsToUpdate = {}) => {
  const overlappedSessionToUpdate = sessionsToUpdate[session.id] || {
    id: session.id,
    horses: [...session.horses],
    instructors: [...session.instructors],
    participants: [...session.participants]
  };

  overlappedSessionToUpdate.horses = overlappedSessionToUpdate.horses.filter(h => h.uuid !== horse.uuid);

  overlappedSessionToUpdate.instructors = overlappedSessionToUpdate.instructors.map(i => {
    if (i.horse === horse.uuid) {
      if (!aditionalContentTypes.includes('instructors')) {
        aditionalContentTypes.push('instructors');
      }
      return { ...i, horse: null };
    }
    return i;
  });

  overlappedSessionToUpdate.participants = overlappedSessionToUpdate.participants.map(p => {
    if (p.horse === horse.uuid) {
      if (!aditionalContentTypes.includes('participants')) {
        aditionalContentTypes.push('participants');
      }
      return { ...p, horse: null };
    }
    return p;
  });

  return overlappedSessionToUpdate;
};

const computeHorsesSessionsToUpdate = (overlappedSessions, sessions, horses, ownerParticipant = null, isInstructor = false, allowSessionSwap = false, { isTimeEdit = false } = {}) => {
  const sessionsToUpdate = {};

  const aditionalContentTypes = [];
  /* Loop all the horses we want to add and get to what overlappedSessions they belong */
  (horses || []).forEach(horse => {
    const sessionsWithHorse = overlappedSessions.filter(overlappedSession => overlappedSession.horses.some(h => h.uuid === horse.uuid));
    (sessions || []).forEach(sessionToAdd => {
      const sessionsWithOverlap = sessionsWithHorse.filter(session => isSessionOverlapping(session, sessionToAdd));

      /* If we dont have any overlapped sessions we just add the horse to every session */
      /* If we dont have any horse overlapped sessions in relation to the session we want to have the horse added, we only need to add the horse to that session */
      if (!overlappedSessions?.length || !sessionsWithOverlap?.length) {
        sessionsToUpdate[sessionToAdd.id] = addHorseToSession(sessionToAdd, horse, sessionsToUpdate, aditionalContentTypes, ownerParticipant, isInstructor, allowSessionSwap);
        return;
      }
      /*
        When we find a overlapped session with that horse we will first check the session priority:
          If the session we want to add the horse has a higher priority we will need to remove the horse from the old session and add it to the new.
          Otherwise we wont do anything and the horse will stay in the old session.
          For time edits (isTimeEdit=true): if the overlapped session wins, we remove the horse from the edited session to avoid double-booking.
      */
      (sessionsWithOverlap || []).forEach(overlappedSession => {
        const hasHigherPriority = (ownerParticipant && horses.some(h => (h.owners || []).some(o => o.ownerUuid === ownerParticipant.uuid))) || compareSessionPriority(overlappedSession, sessionToAdd) || allowSessionSwap;

        if (hasHigherPriority) {
          sessionsToUpdate[overlappedSession.id] = removeHorseFromSession(overlappedSession, horse, aditionalContentTypes, sessionsToUpdate);

          sessionsToUpdate[sessionToAdd.id] = addHorseToSession(sessionToAdd, horse, sessionsToUpdate, aditionalContentTypes, ownerParticipant, isInstructor, allowSessionSwap);
        } else if (isTimeEdit) {
          /* When time editing and overlapped session wins, remove horse from edited session to prevent double-booking */
          sessionsToUpdate[sessionToAdd.id] = removeHorseFromSession(sessionToAdd, horse, aditionalContentTypes, sessionsToUpdate);
        }
      });
    });
  });

  return { modifiedSessions: Object.values(sessionsToUpdate), aditionalContentTypes };
};

async function handleEditSession (sessionToEdit, oldSession, sessionReplica, typeOfUpdate, fieldsToUpdate, { overlapConfirmed = false } = {}) {
  let body = {
    create: [],
    edit: [],
    delete: []
  };
  let errorResponse = null;

  const startTime = sessionsWithPeriods.includes(sessionToEdit.type) ? sessionToEdit.start_time : getUnixTime(startOfDay(fromUnixTime(sessionToEdit.start_time)));

  const sessionToEditBag = await constructSessionsBag(typeOfUpdate, sessionToEdit, startTime);

  const endRecurringDate = new Date(parseISO(sessionReplica.replica_end_date));

  let haveSessionsToCreate = false;
  const recurring_uuid = generateUuid();
  const start_recurring_date = getUnixTime(startOfDay(new Date(fromUnixTime(oldSession.start_recurring_date || sessionToEdit.start_time))));

  if (oldSession.end_recurring_date && format(endOfDay(endRecurringDate), 'yyyy-MM-dd') !== format(fromUnixTime(oldSession.end_recurring_date), 'yyyy-MM-dd')) {
    const isEndRecurringDateAfter = isAfter(endRecurringDate, new Date(fromUnixTime(oldSession.end_recurring_date)));
    haveSessionsToCreate = isEndRecurringDateAfter;

    if (!isEndRecurringDateAfter) {
      const start = endOfDay(endRecurringDate);
      const end = new Date(fromUnixTime(oldSession.end_recurring_date));
      body.delete = sessionToEditBag.filter(session => isWithinInterval(new Date(fromUnixTime(session.start_time)), { start: start, end: end }));
    }
  }

  const isNewSessionReplica = (!oldSession.replica || isObjectEmpty(oldSession.replica)) && (sessionToEdit.replica && !isObjectEmpty(sessionToEdit.replica));

  if (haveSessionsToCreate || isNewSessionReplica) {
    const days = await computeReplicaDays(sessionToEdit, sessionReplica);

    const startDate = oldSession.end_recurring_date || sessionToEdit.end_time;

    const daysToCreate = days.filter(day => isWithinInterval(new Date(day), { start: new Date(fromUnixTime(startDate)), end: add(endRecurringDate, { days: 1 }) }));

    const end_recurring_date = getUnixTime(endOfDay(new Date(daysToCreate[daysToCreate.length - 1])));

    daysToCreate.map(day => {
      const { id, pre_reserved_participants, ...sessionToEditBody } = sessionToEdit;

      const sessionStartTime = getUnixTime(new Date(`${day}T${format(new Date(fromUnixTime(sessionToEdit.start_time)), 'HH:mm')}`));

      if (sessionToEditBody?.periods?.length) {
        const periodsToCreate = sessionToEditBody.periods.filter(p => p.start !== oldSession.start_time && p.end !== oldSession.end_time);

        periodsToCreate.map(period => {
          const periodSessionStartTime = getUnixTime(new Date(`${day}T${format(new Date(fromUnixTime(period.start)), 'HH:mm')}`));
          body.create.push({
            ...sessionToEditBody,
            uuid: computeSessionUuid({ start_time: periodSessionStartTime }),
            id: generateUuid(),
            recurring: true,
            recurring_uuid: sessionToEdit.recurring_uuid || recurring_uuid,
            start_recurring_date,
            end_recurring_date,
            start_time: periodSessionStartTime,
            end_time: getUnixTime(new Date(`${day}T${format(new Date(fromUnixTime(period.end)), 'HH:mm')}`)),
            participants: [],
            place: {},
            horses: [],
            instructors: []
          });
        });
      }

      body.create.push({
        ...sessionToEditBody,
        uuid: computeSessionUuid({ start_time: sessionStartTime }),
        id: generateUuid(),
        recurring: true,
        recurring_uuid: sessionToEdit.recurring_uuid || recurring_uuid,
        start_recurring_date,
        end_recurring_date,
        start_time: sessionStartTime,
        end_time: getUnixTime(new Date(`${day}T${format(new Date(fromUnixTime(sessionToEdit.end_time)), 'HH:mm')}`)),
        participants: [],
        place: {},
        horses: [],
        instructors: []
      });
    });
  }

  const sessionsToEdit = sessionToEditBag.filter(session => !body.delete.some(s => s.id === session.id));

  /* Horse overlap detection for time edits */
  if (!overlapConfirmed && !errorResponse) {
    const hasTimeChange = fieldsToUpdate.some(f => ['start_time', 'end_time'].includes(f));
    const allHorses = sessionsToEdit.flatMap(s => s.horses || []);
    const horses = allHorses.reduce((unique, horse) => (unique.find(h => h.uuid === horse.uuid) ? unique : [...unique, horse]), []);
    const shouldCheckOverlap = hasTimeChange && horses.length > 0;

    if (shouldCheckOverlap) {
      const projectedSessions = applyProjectedFieldsToSessions(sessionsToEdit, sessionToEdit, fieldsToUpdate);
      const horsesUuids = horses.map(h => h.uuid);
      const recurringUuid = sessionToEdit.recurring_uuid && oldSession.recurring ? sessionToEdit.recurring_uuid : null;
      const { selectedDate } = store.getters;
      const { sessionsThisWeek, sessionsNotThisWeek } = separateSessionsByWeek(projectedSessions, selectedDate);

      const overlappedSessions = await computeHorsesOverlappedSessions(horsesUuids, sessionsThisWeek, sessionsNotThisWeek, recurringUuid);

      if (overlappedSessions?.length) {
        const conflicts = await computeHorseOverlapConflicts(projectedSessions, overlappedSessions, horses);

        if (conflicts.length) {
          return {
            success: false,
            pendingOverlapConfirmation: true,
            conflicts,
            closeDialog: false
          };
        }
      }
    }
  }

  sessionsToEdit.map(session => {
    if (errorResponse) return;

    const sessionBody = {};
    const sessionReplicableFields = {};

    fieldsToUpdate.map(field => {
      sessionBody[field] = sessionToEdit[field];
    });

    // replicableFields.map(field => {
    //   sessionReplicableFields[field] = sessionToEdit[field];
    // });

    const currentSessionFieldsChanged = {};

    if (session.id === sessionToEdit.id) {
      fieldsToUpdate.map(field => {
        currentSessionFieldsChanged[field] = sessionToEdit[field];
      });
    }

    fieldsToUpdate.map(field => {
      if (replicableFields.includes(field)) {
        // for date case
        switch (field) {
          case 'start_time':
          case 'end_time': {
            const hours = getHours(fromUnixTime(sessionToEdit[field]));
            const minutes = getMinutes(fromUnixTime(sessionToEdit[field]));

            sessionReplicableFields[field] = getUnixTime(set(fromUnixTime(session[field]), { hours: hours, minutes: minutes }));
            break;
          }
          default: {
            sessionReplicableFields[field] = sessionToEdit[field];
          }
        }
      }
    });

    const needToUpdatePeriod = typeOfUpdate === 'SINGLE' && (oldSession.start_time !== sessionToEdit.start_time || oldSession.end_time !== sessionToEdit.end_time);

    if (session?.periods?.length && needToUpdatePeriod) {
      // there if option is simple and session edit start or end time change we need to add to the bad all the periods
      const { dailySessions } = store.getters;
      const relatedSessions = dailySessions.filter(s => s.recurring_uuid === sessionToEdit.recurring_uuid && s.id !== sessionToEdit.id);

      const periodsOverlapping = relatedSessions.some(s => s.periods.length && sessionToEdit.periods.length && isSessionOverlapping(s, sessionToEdit));
      if (periodsOverlapping) {
        errorResponse = { success: false, message: 'Impossible d\'enregistrer : cette période chevauche une autre période existante du stage.' };
      }

      const currentSessionPeriodIndex = (sessionToEdit?.periods || []).findIndex(p => p.start === oldSession.start_time && p.end === oldSession.end_time);
      if (currentSessionPeriodIndex !== -1) {
        sessionToEdit.periods[currentSessionPeriodIndex].start = sessionToEdit.start_time;
        sessionToEdit.periods[currentSessionPeriodIndex].end = sessionToEdit.end_time;
        currentSessionFieldsChanged.periods = sessionToEdit.periods;
      }

      relatedSessions.map(sess => {
        const periodIndex = sess.periods.findIndex(p => p.start === oldSession.start_time && p.end === oldSession.end_time);

        if (periodIndex !== -1) {
          sess.periods[periodIndex].start = sessionToEdit.start_time;
          sess.periods[periodIndex].end = sessionToEdit.end_time;
        }
        body.edit.push(sess);
      });
    }

    const auxSession = {
      id: session.id,
      ...currentSessionFieldsChanged,
      ...sessionReplicableFields,
      recurring: sessionToEdit.recurring || sessionReplica.isReplica
    };

    if (sessionToEdit.recurring_uuid) {
      auxSession.recurring_uuid = sessionToEdit.recurring_uuid;
    }

    if (sessionToEdit.allDay) {
      auxSession.allDay = sessionToEdit.allDay;
    }

    if (sessionReplica.isReplica) {
      auxSession.recurring_uuid = auxSession.recurring_uuid || recurring_uuid;
      auxSession.start_recurring_date = start_recurring_date;
      auxSession.end_recurring_date = getUnixTime(endOfDay(endRecurringDate));
    }

    if (sessionToEdit.groupeHorseEffort) {
      auxSession.groupeHorseEffort = sessionToEdit.groupeHorseEffort;
    }

    body.edit.push(auxSession);
  });

  /* If user confirmed overlap, apply horse updates */
  if (overlapConfirmed) {
    const hasTimeChange = fieldsToUpdate.some(f => ['start_time', 'end_time'].includes(f));
    const allHorses = sessionsToEdit.flatMap(s => s.horses || []);
    const horses = allHorses.reduce((unique, horse) => (unique.find(h => h.uuid === horse.uuid) ? unique : [...unique, horse]), []);
    const shouldApplyUpdate = hasTimeChange && horses.length > 0;

    if (shouldApplyUpdate) {
      const projectedSessions = applyProjectedFieldsToSessions(sessionsToEdit, sessionToEdit, fieldsToUpdate);
      const horsesUuids = horses.map(h => h.uuid);
      const recurringUuid = sessionToEdit.recurring_uuid && oldSession.recurring ? sessionToEdit.recurring_uuid : null;
      const { selectedDate } = store.getters;
      const { sessionsThisWeek, sessionsNotThisWeek } = separateSessionsByWeek(projectedSessions, selectedDate);

      const overlappedSessions = await computeHorsesOverlappedSessions(horsesUuids, sessionsThisWeek, sessionsNotThisWeek, recurringUuid);

      if (overlappedSessions?.length) {
        const { modifiedSessions } = computeHorsesSessionsToUpdate(overlappedSessions, projectedSessions, horses, null, false, false, { isTimeEdit: true });

        if (modifiedSessions?.length) {
          const mergedModifiedSessions = [];

          (modifiedSessions || []).forEach(modifiedSession => {
            const fullSession = [...sessionsToEdit, ...overlappedSessions].find(s => s.id === modifiedSession.id);

            if (fullSession) {
              mergedModifiedSessions.push({
                ...fullSession,
                horses: modifiedSession.horses,
                participants: modifiedSession.participants,
                instructors: modifiedSession.instructors
              });
            }
          });

          body.edit = mergeSessionUpdatesIntoBody(body.edit, mergedModifiedSessions);
        }
      }
    }
  }

  body = {
    create: body.create.map(s => prepareSessionTransaction(deleteSessionHelpers(s))),
    edit: body.edit.map(s => prepareSessionTransaction(deleteSessionHelpers(s))),
    delete: body.delete.map(s => prepareSessionTransaction(deleteSessionHelpers(s)))
  };

  if (errorResponse) return errorResponse;

  const recurringUuid = sessionToEdit.recurring_uuid && oldSession.recurring ? sessionToEdit.recurring_uuid : null;

  const response = await updateReplicaSessions(body, store.getters.amplifyWorkspace, recurringUuid);
  return { ...response, closeDialog: true };
}

function sessionFieldsToUpdate (oldSession, newSession) {
  const { dailySessions } = store.getters;

  const old = dailySessions.find(s => s.id === oldSession.id);

  const differences = deepDiff(deleteSessionHelpers(newSession), deleteSessionHelpers(old));

  const changedFields = (differences || []).map(field => field.path[0]);

  return [...new Set(changedFields)];
}

function compareSessions (oldSession, newSession, fields) {
  const differences = deepDiff(deleteSessionHelpers(newSession), deleteSessionHelpers(oldSession));

  const changedFields = (differences || []).map(field => field.path[0]);

  return [...new Set(changedFields)];
}

const constructSessionsToUpdate = async (sessionId, updateType = null, endDate = null) => {
  const session = await getSessionById(sessionId);

  if (!session) {
    sendNotification('error', 'erreur lors de la mise à jour de la session');
    return [];
  }

  let sessions = [session];
  if (session.recurring && (session.replica || sessionsWithPeriods.includes(session.type))) {
    if (updateType) {
      sessions = await constructSessionsBag(updateType, session, undefined, endDate);
    }
  }
  return sessions;
};

const sessionByWorkspaceActivities = sessionType => {
  const { workspaceActivitiesFilter } = store.state;

  if (!workspaceActivitiesFilter || !workspaceActivitiesFilter.length) {
    return true;
  }

  return workspaceActivitiesFilter.some(activity => activityToSessionTypes[activity].includes(sessionType));
};

const equestrianSession = sessionType => activityToSessionTypes.EQUITATION.includes(sessionType);

const golfSession = sessionType => activityToSessionTypes.GOLF.includes(sessionType);

const removeHorseAndUpdate = async (session, horse) => {
  const sessionsToUpdate = {};
  const aditionalContentTypes = [];

  sessionsToUpdate[session.id] = removeHorseFromSession(session, horse, aditionalContentTypes);

  const modifiedSessions = Object.values(sessionsToUpdate);
  if (modifiedSessions?.length) {
    return updateSessionsBulk(modifiedSessions, ['horses', ...aditionalContentTypes]);
  }
  return false;
};

const swapHorseSession = async (overlappedSessions, session, horse, skipEffortCheck) => {
  if (!skipEffortCheck) {
    const horsesAlertEffort = await checkHorseDailyEffort([horse], [session], (overlappedSessions || []).map(s => s.id));
    if (Object.keys(horsesAlertEffort).length) {
      store.commit('setAlertEffort', horsesAlertEffort);
      store.commit('setShowMaxEffortModal', true);
      store.commit('setMaxEffortCallback', () => swapHorseSession(overlappedSessions, session, horse, { skipEffortCheck: true }));
      return NO_UPDATE;
    }
  }

  let contentTypes = ['horses'];

  const { modifiedSessions, aditionalContentTypes } = computeHorsesSessionsToUpdate(overlappedSessions, [session], [horse], null, !!horse.instructor, true);
  if (modifiedSessions?.length) {
    contentTypes = [...contentTypes, ...aditionalContentTypes];
    return updateSessionsBulk(modifiedSessions, contentTypes);
  }
  return false;
};

export {
  compareSessions,
  getOverlappedSessions,
  prepareSessionTransaction,
  sessionFieldsToUpdate,
  handleEditSession,
  handleCreateSession,
  prepareSessionBody,
  handleDeleteSession,
  handleSessionsCreation,
  sessionHaveError,
  computePeriodSession,
  addPeriodToSessionPeriodArray,
  isParticipantAmountOk,
  isEndTimeAllowedToCreate,
  longSessionTypes,
  sessionTypeToNotShowBasedOnTab,
  sessionsWithPeriods,
  getReferencesSessionCreate,
  getSessionFieldByWtype,
  getContentTypeSessionsByPeriod,
  filterSessionByInterval,
  sessionContentType,
  constructSessionsBag,
  getDatesBetweenDates,
  computeSessionUuid,
  constructSessionsToUpdate,
  updateSessionsBulk,
  recurringHorseAssignSessions,
  sessionsHorsesPriority,
  computeHorsesSessionsToUpdate,
  activityToSessionTypes,
  sessionByWorkspaceActivities,
  equestrianSession,
  golfSession,
  canCopyHorses,
  removeHorseAndUpdate,
  swapHorseSession,
  NO_UPDATE
};
